import React from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import useTimezoneDate from "../../../../../hooks/useTimezoneDate";
import { Country } from "../../../../../models/country";
import { DifferentLocation, Location } from "../../../../../models/location";
import { CommercialInvoice } from "../../../../../util/enums";
import AutoSuggest from "../../../../Form/AutoSuggest";
import Input from "../../../../Form/Input";
import AddressInfoForm from "../../../../Generic/AddressComponent/AddressInfoForm";
import Skeleton from "../../../../Generic/Skeleton";
import { useBookingResourcesContext } from "../../../hooks/useResourcesContext";
import useStyles from "../styles";
import { CommercialInvoiceFormProps } from "../types";

export const CommercialInvoiceGeneral = ({
  commercialInvoice,
  viewOnly,
  fieldHasError,
  getErrorByField,
  updateComercialInvoice,
  loadingBooking = true,
  errorResponse,
}: CommercialInvoiceFormProps) => {
  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const { countryList, loading: loadingResources } = useBookingResourcesContext();
  const classes = useStyles();

  const countrySelected = countryList?.find(
    (country) => country?.id === commercialInvoice?.countryId
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.title}>
        <span>{translate("commercialInvoiceDetails")}</span>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            // required
            data-cy={CommercialInvoice.INVOICE_NUMBER}
            name={CommercialInvoice.INVOICE_NUMBER}
            value={commercialInvoice?.invoiceNumber || ""}
            label={`${translate("invoice")} #`}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.INVOICE_NUMBER, event.target.value)
            }
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.INVOICE_NUMBER)}
            error={fieldHasError(CommercialInvoice.INVOICE_NUMBER)}
            errorMessage={getErrorByField(CommercialInvoice.INVOICE_NUMBER)}
            errorResponse={errorResponse}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.BENEFICIARY_NAME}
            name={CommercialInvoice.BENEFICIARY_NAME}
            value={commercialInvoice?.beneficiaryName || ""}
            label={translate(CommercialInvoice.BENEFICIARY_NAME)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.BENEFICIARY_NAME, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.BENEFICIARY_NAME)}
            errorMessage={getErrorByField(CommercialInvoice.BENEFICIARY_NAME)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.BENEFICIARY_NAME)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <AddressInfoForm
            required
            dataCy={CommercialInvoice.BENEFICIARY_ADDRESS}
            addressesArray={[(commercialInvoice?.beneficiaryAddress || []) as DifferentLocation]}
            view={viewOnly}
            showMapIcon={false}
            isExpandedAddress={true}
            condition=""
            update={(value) =>
              updateComercialInvoice(CommercialInvoice.BENEFICIARY_ADDRESS, value[0] as Location)
            }
            hasToShowOnlyOne={true}
            title={translate(CommercialInvoice.BENEFICIARY_ADDRESS)}
            hasError={fieldHasError(CommercialInvoice.BENEFICIARY_ADDRESS)}
            errorMessage={getErrorByField(CommercialInvoice.BENEFICIARY_ADDRESS)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}></Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.VENDOR_ID}
            name={CommercialInvoice.VENDOR_ID}
            value={commercialInvoice?.vendorId || ""}
            label={translate(CommercialInvoice.VENDOR_ID)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.VENDOR_ID, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.VENDOR_ID)}
            errorMessage={getErrorByField(CommercialInvoice.VENDOR_ID)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.VENDOR_ID)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.PAYMENT_TERMS}
            name={CommercialInvoice.PAYMENT_TERMS}
            value={commercialInvoice?.paymentTerms || ""}
            label={translate(CommercialInvoice.PAYMENT_TERMS)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.PAYMENT_TERMS, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.PAYMENT_TERMS)}
            errorMessage={getErrorByField(CommercialInvoice.PAYMENT_TERMS)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.PAYMENT_TERMS)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.FISCAL_AGENT_DETAILS}
            name={CommercialInvoice.FISCAL_AGENT_DETAILS}
            value={commercialInvoice?.fiscalAgentDetails || ""}
            label={translate(CommercialInvoice.FISCAL_AGENT_DETAILS)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.FISCAL_AGENT_DETAILS, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.FISCAL_AGENT_DETAILS)}
            errorMessage={getErrorByField(CommercialInvoice.FISCAL_AGENT_DETAILS)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.FISCAL_AGENT_DETAILS)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking || loadingResources}>
          <AutoSuggest
            required
            label={translate("countryOfOrigin")}
            disabled={viewOnly}
            placeholder={translate("searchByCountry")}
            inputValue={countrySelected?.name}
            options={countryList}
            error={!!getErrorByField("countryId")}
            errorMessage={getErrorByField("countryId")}
            infoMessage={translate("commercialInvoiceCountryOfOriginHelperText")}
            getOptionLabel={(country: Country) => String(country?.name)}
            getOptionSelected={(option: Country, value: Country) =>
              option && value && option.id === value.id
            }
            onChange={(value: Country) => {
              updateComercialInvoice(CommercialInvoice.COUNTRY_ID, value?.id);
            }}
          />
        </Skeleton>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.SALES_CONTRACT_NUMBER}
            name={CommercialInvoice.SALES_CONTRACT_NUMBER}
            value={commercialInvoice?.salesContractNumber || ""}
            label={translate(CommercialInvoice.SALES_CONTRACT_NUMBER)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.SALES_CONTRACT_NUMBER, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.SALES_CONTRACT_NUMBER)}
            errorMessage={getErrorByField(CommercialInvoice.SALES_CONTRACT_NUMBER)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.SALES_CONTRACT_NUMBER)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.SALES_CONTRACT_DATE}
            type="date"
            name={CommercialInvoice.SALES_CONTRACT_DATE}
            value={
              commercialInvoice?.salesContractDate &&
              !isNaN(new Date(commercialInvoice.salesContractDate).getTime())
                ? getDateValueUtcTimezone(commercialInvoice.salesContractDate)
                : "-"
            }
            label={translate(CommercialInvoice.SALES_CONTRACT_DATE)}
            onChange={(event) =>
              updateComercialInvoice(
                CommercialInvoice.SALES_CONTRACT_DATE,
                new Date(event.target.value).toISOString()
              )
            }
            error={fieldHasError(CommercialInvoice.SALES_CONTRACT_DATE)}
            errorMessage={getErrorByField(CommercialInvoice.SALES_CONTRACT_DATE)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.SALES_CONTRACT_DATE)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.EXP_NUMBER}
            name={CommercialInvoice.EXP_NUMBER}
            value={commercialInvoice?.expNumber || ""}
            label={translate(CommercialInvoice.EXP_NUMBER)}
            onChange={(event) =>
              updateComercialInvoice(CommercialInvoice.EXP_NUMBER, event.target.value)
            }
            error={fieldHasError(CommercialInvoice.EXP_NUMBER)}
            errorMessage={getErrorByField(CommercialInvoice.EXP_NUMBER)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.EXP_NUMBER)}
          />
        </Skeleton>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Skeleton isLoading={loadingBooking}>
          <Input
            required
            data-cy={CommercialInvoice.EXP_DATE}
            type="date"
            name={CommercialInvoice.EXP_DATE}
            value={
              commercialInvoice?.expDate && !isNaN(new Date(commercialInvoice?.expDate).getTime())
                ? getDateValueUtcTimezone(commercialInvoice?.expDate)
                : "-"
            }
            label={translate(CommercialInvoice.EXP_DATE)}
            onChange={(event) =>
              updateComercialInvoice(
                CommercialInvoice.EXP_DATE,
                new Date(event.target.value).toISOString()
              )
            }
            error={fieldHasError(CommercialInvoice.EXP_DATE)}
            errorMessage={getErrorByField(CommercialInvoice.EXP_DATE)}
            disabled={viewOnly}
            placeholder={translate(CommercialInvoice.EXP_DATE)}
          />
        </Skeleton>
      </Grid>
    </Grid>
  );
};
